/**
 * components/landing/home/journey/illustrations.jsx — Phase 2.3 · one small
 * visual per Customer Success Journey step.
 *
 * Every illustration is:
 *   \u2022 Self-contained (no external assets — just Tailwind + a couple of
 *     lucide icons + inline SVG).
 *   \u2022 Sized to fit ~360x300 comfortably inside the StepCard.
 *   \u2022 Given a light "activate" animation when the step becomes active
 *     (the parent card toggles a class we listen for via CSS).
 *
 * These are intentionally illustrative — not full mini-dashboards.  The
 * heavy dashboards live in the Phase 2.1 (Interactive Product) and 2.2
 * (Architecture) sections.
 */

import React from 'react'
import clsx from 'clsx'
import {
  Wallet, CreditCard, ServerCog, Palette, Globe, ToggleRight, UsersRound,
  Rocket, TrendingUp, Check, CheckCircle2, Lock, Shield, ArrowUpRight,
  Sparkles, Zap, ShieldCheck, Cog, KeyRound, User, Cpu, PartyPopper,
  ChevronRight, RefreshCw, Star, BookOpen, LineChart, ScanLine, Wand2,
  GraduationCap, BarChart3, Building2,
} from 'lucide-react'

// ── shared frame ─────────────────────────────────────────────────────
const Frame = ({ children, tone, className = '' }) => (
  <div
    className={clsx(
      'relative w-full h-full min-h-[280px] rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] overflow-hidden',
      className,
    )}
  >
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: `radial-gradient(70% 45% at 30% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }}
    />
    <div className="relative h-full p-5">{children}</div>
  </div>
)

// ─── Step 1 · Choose Plan ────────────────────────────────────────────
const PlanIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-3">
      <span>PLANS</span>
      <span>USD</span>
    </div>
    <div className="grid grid-cols-3 gap-2 h-[210px]">
      {[
        { n: 'Quarterly', p: '$25,000', h: 70,  active: false },
        { n: 'Half-Year', p: '$40,000', h: 100, active: true,  save: 'save 20%' },
        { n: 'Annual', p: '$60,000', h: 85,  active: false, save: 'save 40%' },
      ].map((c) => (
        <div
          key={c.n}
          className={clsx(
            'relative flex flex-col justify-end rounded-xl border p-3 transition-all',
            c.active
              ? 'border-brand-gold/40 bg-gradient-to-b from-brand-gold/[0.10] to-transparent shadow-[0_20px_60px_-30px_rgba(255,193,7,0.6)]'
              : 'border-white/10 bg-white/[0.02]',
          )}
        >
          {c.save && (
            <span className="absolute -top-2 left-3 rounded-full border border-brand-gold/40 bg-black px-1.5 py-0.5 text-[9px] font-semibold text-brand-gold">
              {c.save}
            </span>
          )}
          <div className="flex-1 relative">
            <div
              className={clsx('absolute bottom-0 left-1/2 w-2 rounded-full -translate-x-1/2',
                c.active ? 'bg-brand-gold' : 'bg-white/20',
              )}
              style={{ height: `${c.h}%` }}
            />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{c.n}</div>
          <div className={clsx('text-sm font-semibold', c.active ? 'text-white' : 'text-zinc-300')}>{c.p}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-gold/10 border border-brand-gold/30 px-2.5 py-1 text-[10.5px] font-semibold text-brand-gold">
      <Check className="h-3 w-3" /> Half-Year selected
    </div>
  </Frame>
)

// ─── Step 2 · Stripe Checkout ────────────────────────────────────────
const CheckoutIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#635BFF]/20 border border-[#635BFF]/40">
          <span className="text-[9px] font-bold text-[#a5a2ff]">S</span>
        </div>
        <span className="text-[11px] font-semibold text-white">Stripe checkout</span>
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400"><Lock className="h-3 w-3" /> encrypted</span>
    </div>
    <div className="flex flex-col gap-2">
      <div className="rounded-lg border border-white/10 bg-black/40 p-3">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Order summary</div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-white">Kalki Ai Cloud White-Label · Half-Year</span>
          <span className="font-mono font-semibold text-white">$40,000</span>
        </div>
        <div className="mt-1 text-[10px] text-zinc-500">Taxes calculated at checkout</div>
      </div>
      <div className="rounded-lg border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="h-8 flex-1 rounded-md bg-white/[0.03] flex items-center px-3 text-[11px] font-mono text-zinc-400">
            4242 &nbsp; 4242 &nbsp; 4242 &nbsp; <span className="text-white">4242</span>
          </div>
          <div className="h-8 w-16 rounded-md bg-white/[0.03] flex items-center justify-center text-[11px] font-mono text-white">12/29</div>
          <div className="h-8 w-14 rounded-md bg-white/[0.03] flex items-center justify-center text-[11px] font-mono text-white">•••</div>
        </div>
        <button className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#635BFF] py-2 text-[12px] font-semibold text-white">
          <Lock className="h-3 w-3" /> Pay $40,000
        </button>
      </div>
      <div className="flex items-center gap-2 text-[10px] text-emerald-400 font-mono">
        <CheckCircle2 className="h-3.5 w-3.5" /> Payment succeeded · licence activated
      </div>
    </div>
  </Frame>
)

// ─── Step 3 · Provisioning ───────────────────────────────────────────
const ProvisioningIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
        <ServerCog className="h-3.5 w-3.5 text-sky-400" /> Provisioning · tenant-8241
      </div>
      <span className="text-[10px] font-mono text-emerald-400">running</span>
    </div>
    <div className="flex flex-col gap-2 font-mono text-[11px]">
      {[
        { t: 'Allocate database', ok: true },
        { t: 'Provision AI Mentor knowledge base', ok: true },
        { t: 'Generate admin credentials', ok: true },
        { t: 'Apply licence entitlements', ok: true },
        { t: 'Warm caches · 3 regions', p: 62, active: true },
        { t: 'Verify tenant health', pending: true },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
          {r.ok
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
            : r.active
              ? <RefreshCw className="h-3.5 w-3.5 text-sky-400 animate-spin shrink-0" />
              : <div className="h-3.5 w-3.5 rounded-full border border-white/20 shrink-0" />}
          <span className={clsx('flex-1', r.pending ? 'text-zinc-500' : 'text-zinc-200')}>{r.t}</span>
          {r.p !== undefined && (
            <div className="flex items-center gap-1.5">
              <div className="h-1 w-16 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-sky-400" style={{ width: `${r.p}%` }} />
              </div>
              <span className="text-[9px] text-sky-400 w-6 text-right">{r.p}%</span>
            </div>
          )}
        </div>
      ))}
    </div>
    <div className="mt-3 text-[10px] font-mono text-zinc-500">Region · ap-south-1 · fully automated</div>
  </Frame>
)

// ─── Step 4 · Branding ───────────────────────────────────────────────
const BrandingIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
        <Palette className="h-3.5 w-3.5 text-violet-400" /> Brand studio
      </div>
      <span className="text-[10px] font-mono text-zinc-500">theme · acme</span>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <div className="col-span-2 rounded-lg border border-white/10 bg-black/40 p-3">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-2">Logo &amp; wordmark</div>
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[#a855f7] to-[#22d3ee] flex items-center justify-center">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-white leading-tight">Acme Trading</div>
            <div className="text-[10px] text-zinc-500">acme.io</div>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-white/10 bg-black/40 p-3 flex flex-col items-center justify-center gap-1">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500">Favicon</div>
        <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[#a855f7] to-[#22d3ee] flex items-center justify-center">
          <span className="text-white font-bold text-[10px]">A</span>
        </div>
      </div>
    </div>
    <div className="mt-2 rounded-lg border border-white/10 bg-black/40 p-3">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-2">Palette</div>
      <div className="grid grid-cols-5 gap-1.5">
        {['#a855f7', '#22d3ee', '#22c55e', '#FFC107', '#0f172a'].map((c) => (
          <div key={c} className="rounded-md h-8 border border-white/10" style={{ background: c }} />
        ))}
      </div>
    </div>
    <div className="mt-2 rounded-lg border border-white/10 bg-black/40 p-2.5 text-[10px] text-zinc-400">
      Email template · <span className="text-white">Welcome to Acme Trading — your workspace is ready</span>
    </div>
  </Frame>
)

// ─── Step 5 · Domain ─────────────────────────────────────────────────
const DomainIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
        <Globe className="h-3.5 w-3.5 text-cyan-400" /> Custom domain
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400">
        <ShieldCheck className="h-3 w-3" /> SSL live
      </span>
    </div>
    <div className="rounded-lg border border-white/10 bg-black/40 p-3 mb-2">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Platform URL</div>
      <div className="flex items-center gap-2 font-mono text-sm">
        <Lock className="h-3.5 w-3.5 text-emerald-400" />
        <span className="text-emerald-400">https://</span>
        <span className="text-white">platform.acme.io</span>
      </div>
      <div className="mt-1 text-[10px] text-zinc-500">TLS 1.3 · HSTS · auto-renews every 60d</div>
    </div>
    <div className="flex flex-col gap-1.5 font-mono text-[11px]">
      {[
        { t: 'CNAME  →  tenants.kalkiai.trade', ok: true },
        { t: 'DNS validation',                ok: true },
        { t: 'SSL certificate issued',        ok: true },
        { t: 'CDN edge cache warmed',         ok: true },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
          <span className="text-zinc-200">{r.t}</span>
        </div>
      ))}
    </div>
  </Frame>
)

// ─── Step 6 · Modules ────────────────────────────────────────────────
const ModulesIllustration = ({ tone }) => {
  const MODS = [
    { n: 'AI Mentor',        i: Sparkles,       on: true,  tone: 'orange' },
    { n: 'Trading Terminal', i: LineChart,      on: true,  tone: 'green' },
    { n: 'Strategy Builder', i: Wand2,          on: true,  tone: 'purple' },
    { n: 'Backtesting',      i: BarChart3,      on: true,  tone: 'blue' },
    { n: 'Trading Academy',  i: GraduationCap,  on: false, tone: 'purple' },
    { n: 'Scanner',          i: ScanLine,       on: true,  tone: 'orange' },
    { n: 'Analytics',        i: BarChart3,      on: true,  tone: 'blue' },
    { n: 'White-Label',      i: Building2,      on: true,  tone: 'cyan' },
  ]
  return (
    <Frame tone={tone}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
          <ToggleRight className="h-3.5 w-3.5 text-brand-gold" /> Modules
        </div>
        <span className="text-[10px] font-mono text-emerald-400">7 of 8 active</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {MODS.map((m) => (
          <div key={m.n} className={clsx('flex items-center gap-2 rounded-md border px-2 py-2',
            m.on ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60',
          )}>
            <m.i className={clsx('h-3.5 w-3.5',
              m.on && m.tone === 'orange' && 'text-brand-gold',
              m.on && m.tone === 'green'  && 'text-emerald-400',
              m.on && m.tone === 'blue'   && 'text-sky-400',
              m.on && m.tone === 'purple' && 'text-violet-400',
              m.on && m.tone === 'cyan'   && 'text-cyan-400',
              !m.on && 'text-zinc-500',
            )} />
            <span className={clsx('flex-1 text-[11px]', m.on ? 'text-white' : 'text-zinc-500')}>{m.n}</span>
            <div className={clsx('h-3 w-6 rounded-full transition-all relative',
              m.on ? 'bg-brand-gold/70' : 'bg-white/10',
            )}>
              <span className={clsx('absolute top-0.5 h-2 w-2 rounded-full bg-white transition-all',
                m.on ? 'right-0.5' : 'left-0.5',
              )} />
            </div>
          </div>
        ))}
      </div>
    </Frame>
  )
}

// ─── Step 7 · Team & Access ──────────────────────────────────────────
const TeamIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
        <UsersRound className="h-3.5 w-3.5 text-emerald-400" /> Team · 6 members
      </div>
      <span className="text-[10px] font-mono text-emerald-400">SSO · Okta</span>
    </div>
    <div className="flex flex-col gap-1.5">
      {[
        { n: 'Priya S.',   e: 'priya@acme.io',   r: 'Admin',    tone: 'orange' },
        { n: 'James R.',   e: 'james@acme.io',   r: 'Ops',      tone: 'blue' },
        { n: 'Amara N.',   e: 'amara@acme.io',   r: 'Analyst',  tone: 'purple' },
        { n: 'David L.',   e: 'david@acme.io',   r: 'Trader',   tone: 'green' },
        { n: 'Elena V.',   e: 'elena@acme.io',   r: 'Read-only', tone: 'gray' },
      ].map((u, i) => (
        <div key={u.e} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2">
          <div
            className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ background: `linear-gradient(135deg, hsl(${i * 47}, 60%, 45%), hsl(${i * 47 + 40}, 70%, 35%))` }}
          >
            {u.n.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] text-white truncate">{u.n}</div>
            <div className="text-[9.5px] font-mono text-zinc-500 truncate">{u.e}</div>
          </div>
          <span className={clsx('rounded-full border px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider',
            u.tone === 'orange' && 'border-brand-gold/40 text-brand-gold bg-brand-gold/10',
            u.tone === 'green'  && 'border-emerald-400/40 text-emerald-400 bg-emerald-400/10',
            u.tone === 'blue'   && 'border-sky-400/40 text-sky-400 bg-sky-400/10',
            u.tone === 'purple' && 'border-violet-400/40 text-violet-400 bg-violet-400/10',
            u.tone === 'gray'   && 'border-white/20 text-zinc-400 bg-white/5',
          )}>{u.r}</span>
        </div>
      ))}
    </div>
    <div className="mt-2 text-[10px] font-mono text-zinc-500">+ 1 invite pending · audit log · 90d</div>
  </Frame>
)

// ─── Step 8 · Launch ─────────────────────────────────────────────────
const LaunchIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="absolute inset-0 pointer-events-none"
      style={{ background: 'radial-gradient(50% 50% at 50% 40%, rgba(255,193,7,0.20), transparent 70%)' }} />
    <div className="relative flex flex-col items-center justify-center h-full gap-4 text-center">
      <div className="relative">
        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-brand-gold to-[#FFE17A] flex items-center justify-center shadow-[0_20px_60px_-10px_rgba(255,193,7,0.7)]">
          <Rocket className="h-8 w-8 text-black" />
        </div>
        <span className="absolute -top-2 -right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 border-2 border-black">
          <Check className="h-3.5 w-3.5 text-white" />
        </span>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-brand-gold font-semibold">go live</div>
        <div className="text-xl font-semibold text-white mt-1">platform.acme.io is live</div>
        <div className="text-xs text-zinc-400 mt-1">All systems operational · 4 regions</div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {[
          'Branding applied',
          'Domain connected',
          'SSL active',
          '7 modules on',
          '6 team seats',
        ].map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> {t}
          </span>
        ))}
      </div>
    </div>
  </Frame>
)

// ─── Step 9 · Growth ─────────────────────────────────────────────────
const GrowthIllustration = ({ tone }) => (
  <Frame tone={tone}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-white">
        <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Continuous improvement
      </div>
      <span className="text-[10px] font-mono text-emerald-400">this quarter</span>
    </div>
    <div className="flex flex-col gap-1.5">
      {[
        { d: 'Jun 2026', t: 'AI Mentor · GPT-4o upgrade',         tone: 'orange' },
        { d: 'May 2026', t: 'New scanner · 24/7 alerting',         tone: 'blue' },
        { d: 'May 2026', t: 'Trading Academy · 12 new lessons',    tone: 'purple' },
        { d: 'Apr 2026', t: 'Backtesting · walk-forward optimiser', tone: 'green' },
        { d: 'Ongoing',  t: 'Dedicated CS partner + monitoring',         tone: 'cyan' },
      ].map((r, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2">
          <span className={clsx('mt-1 h-1.5 w-1.5 shrink-0 rounded-full',
            r.tone === 'orange' && 'bg-brand-gold',
            r.tone === 'blue'   && 'bg-sky-400',
            r.tone === 'purple' && 'bg-violet-400',
            r.tone === 'green'  && 'bg-emerald-400',
            r.tone === 'cyan'   && 'bg-cyan-400',
          )} />
          <div className="flex-1">
            <div className="text-[11px] text-white">{r.t}</div>
            <div className="text-[9.5px] font-mono text-zinc-500">{r.d}</div>
          </div>
          <Star className={clsx('h-3 w-3',
            r.tone === 'orange' && 'text-brand-gold',
            r.tone === 'blue'   && 'text-sky-400',
            r.tone === 'purple' && 'text-violet-400',
            r.tone === 'green'  && 'text-emerald-400',
            r.tone === 'cyan'   && 'text-cyan-400',
          )} />
        </div>
      ))}
    </div>
  </Frame>
)

// ── Registry ─────────────────────────────────────────────────────────
export const ILLUSTRATIONS = {
  plan:         PlanIllustration,
  checkout:     CheckoutIllustration,
  provisioning: ProvisioningIllustration,
  branding:     BrandingIllustration,
  domain:       DomainIllustration,
  modules:      ModulesIllustration,
  team:         TeamIllustration,
  launch:       LaunchIllustration,
  growth:       GrowthIllustration,
}

// ── Icon lookup for step headers ─────────────────────────────────────
export const STEP_ICONS = {
  Wallet, CreditCard, ServerCog, Palette, Globe, ToggleRight, UsersRound,
  Rocket, TrendingUp,
}
